import React from "react";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import ReplayIcon from "@mui/icons-material/Replay";
import { T, SectionGroup, computeChecklistResult, groupItemsIntoSections } from "./checklistScoring";

const VERDICT = {
  pass: { label: "Pass", note: "Passing score reached.", bg: "var(--color-success-bg)", fg: "var(--color-success)" },
  fail: { label: "Fail", note: "Score below passing or a critical criterion missed.", bg: "var(--color-danger-bg)", fg: "var(--color-danger)" },
  pending: { label: "In progress", note: "Score every criterion to finish.", bg: "var(--color-surface-press-light)", fg: "var(--color-ink)" },
};
const SEG_COLOR = { performed: "var(--color-success)", missed: "var(--color-danger)", na: "var(--color-hairline-cool)" };
const KEY_SCORE = { p: "performed", m: "missed", n: "na" };

/**
 * Shared shell for scoring a skill checklist — header (badge/title/timer),
 * section groups, and slots for mode-specific chrome (header identity block,
 * footer meta fields, bottom action bar). Owns scores/notes/timer/meta state
 * so both the peer-practice and official instructor screens share one
 * implementation of the actual scoring UI and timer behavior; `onSave` is
 * called directly by this component (manual save button via ctx.save(), or
 * automatically on timeout when autoSubmitOnTimeout is set) so callers never
 * need to reach into child-rendered DOM or juggle stale closures themselves.
 */
export default function ChecklistAssessmentScreen({
  checklist,
  badgeLabel,
  badgeColor,
  locked = false,
  initialScores,
  initialNotes,
  initialMeta,
  onSave,
  headerRight,
  autoSubmitOnTimeout = false,
  onTimeout,
  footer,
  bottomBar,
  rail,
}) {
  const theme = useTheme();
  const resolvedBadgeColor = badgeColor ?? theme.palette.warning.main;
  const items = React.useMemo(() => checklist?.items || [], [checklist]);
  const sections = React.useMemo(() => groupItemsIntoSections(items), [items]);

  const [scores, setScores] = React.useState(initialScores || {});
  const [notes, setNotes] = React.useState(initialNotes || {});
  const [meta, setMetaState] = React.useState(initialMeta || {});
  const [examDate, setExamDate] = React.useState(new Date().toISOString().slice(0, 10));
  const [startTime, setStartTime] = React.useState("");
  const [elapsed, setElapsed] = React.useState(0);
  const [running, setRunning] = React.useState(false);
  const [timeExpired, setTimeExpired] = React.useState(false);
  const hasStarted = React.useRef(false);
  const autoEnded = React.useRef(false);

  const setMeta = (patch) => setMetaState((m) => ({ ...m, ...patch }));

  React.useEffect(() => {
    if (initialScores) setScores(initialScores);
    // eslint-disable-next-line
  }, [initialScores]);
  React.useEffect(() => {
    if (initialNotes) setNotes(initialNotes);
    // eslint-disable-next-line
  }, [initialNotes]);
  React.useEffect(() => {
    if (initialMeta) setMetaState((m) => ({ ...m, ...initialMeta }));
    // eslint-disable-next-line
  }, [initialMeta]);

  // Mirrors of state the countdown interval reads — the interval's closure is
  // fixed to whenever `running` last toggled, so plain state would be stale.
  const scoresRef = React.useRef(scores);
  const notesRef = React.useRef(notes);
  const metaRef = React.useRef(meta);
  const examDateRef = React.useRef(examDate);
  const startTimeRef = React.useRef(startTime);
  React.useEffect(() => { scoresRef.current = scores; }, [scores]);
  React.useEffect(() => { notesRef.current = notes; }, [notes]);
  React.useEffect(() => { metaRef.current = meta; }, [meta]);
  React.useEffect(() => { examDateRef.current = examDate; }, [examDate]);
  React.useEffect(() => { startTimeRef.current = startTime; }, [startTime]);

  const timeLimitSec = (checklist?.timeLimitMin || 0) * 60;
  const hasLimit = timeLimitSec > 0;

  const save = (elapsedSec, forcedScores) => {
    if (!onSave) return;
    const d = new Date();
    onSave({
      ...metaRef.current,
      scores: forcedScores || scoresRef.current,
      notes: notesRef.current,
      examDate: examDateRef.current,
      startTime: startTimeRef.current || `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`,
      endTime: `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`,
      elapsedSec,
    });
  };

  const markStart = () => {
    if (hasStarted.current) return;
    hasStarted.current = true;
    setRunning(true);
    const d = new Date();
    setStartTime(`${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`);
  };

  React.useEffect(() => {
    if (!running) return;
    const t = setInterval(() => {
      setElapsed((e) => {
        const next = e + 1;
        if (hasLimit && next >= timeLimitSec && !autoEnded.current) {
          autoEnded.current = true;
          setRunning(false);
          setTimeExpired(true);
          let filled = null;
          if (autoSubmitOnTimeout) {
            filled = { ...scoresRef.current };
            items.forEach((item) => { if (filled[item.id] == null) filled[item.id] = "missed"; });
            setScores(filled);
          }
          if (onTimeout) onTimeout({ autoSubmitted: autoSubmitOnTimeout, elapsedSec: next });
          if (autoSubmitOnTimeout) save(next, filled);
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [running, hasLimit, timeLimitSec, autoSubmitOnTimeout]);

  const setScore = (id, v) => {
    markStart();
    setScores((s) => (v === null ? (() => { const c = { ...s }; delete c[id]; return c; })() : { ...s, [id]: v }));
  };
  const setNote = (id, v) => setNotes((n) => ({ ...n, [id]: v }));
  const toggleTimer = () => {
    markStart();
    setRunning((r) => !r);
  };
  const resetTimer = () => {
    setRunning(false);
    setElapsed(0);
    setTimeExpired(false);
    autoEnded.current = false;
    hasStarted.current = false;
  };

  const r = computeChecklistResult(items, scores, checklist?.passingScore);
  const remainingSec = hasLimit ? Math.max(0, timeLimitSec - elapsed) : elapsed;
  const timeUp = hasLimit && remainingSec <= 0;
  const lowTime = hasLimit && remainingSec <= 30 && remainingSec > 0;
  const mm = String(Math.floor(remainingSec / 60)).padStart(2, "0");
  const ss = String(remainingSec % 60).padStart(2, "0");

  const [currentId, setCurrentId] = React.useState(null);
  const focusId = currentId ?? items[0]?.id ?? null;

  React.useEffect(() => {
    if (locked || timeExpired) return undefined;
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey || /INPUT|TEXTAREA|SELECT|BUTTON/.test(e.target.tagName) || e.target.isContentEditable) return;
      const idx = items.findIndex((it) => it.id === focusId);
      const k = e.key.toLowerCase();
      if (KEY_SCORE[k] && idx > -1) {
        e.preventDefault();
        const v = KEY_SCORE[k];
        const wasSet = scoresRef.current[focusId] === v;
        setScore(focusId, wasSet ? null : v);
        const next = items.findIndex((it, j) => j > idx && scoresRef.current[it.id] == null);
        if (!wasSet && next > -1) setCurrentId(items[next].id);
      } else if (k === "arrowdown" || k === "j") {
        e.preventDefault();
        setCurrentId(items[Math.min(items.length - 1, idx + 1)]?.id ?? null);
      } else if (k === "arrowup" || k === "k") {
        e.preventDefault();
        setCurrentId(items[Math.max(0, idx - 1)]?.id ?? null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line
  }, [items, focusId, locked, timeExpired]);

  const ctx = {
    scores, notes, setScore, setNote,
    meta, setMeta,
    r, elapsed, running, timeExpired,
    examDate, setExamDate, startTime, setStartTime,
    canSaveDefault: r.scoredCount > 0,
    save: () => save(elapsed),
  };

  const verdict = VERDICT[r.status] || VERDICT.pending;
  const missedCount = items.filter((it) => scores[it.id] === "missed").length;
  const criticalCount = items.filter((it) => it.isCritical).length;
  const criticalMet = items.filter((it) => it.isCritical && scores[it.id] === "performed").length;
  const left = items.length - r.scoredCount;
  const eyebrow = { fontSize: 10.5, fontWeight: 700, letterSpacing: ".12em", textTransform: "uppercase", opacity: 0.6 };
  const iconBtn = { width: 36, height: 36, borderRadius: "8px", border: 0, bgcolor: "transparent", color: "var(--color-on-dark-muted)", cursor: "pointer", display: "inline-grid", placeItems: "center", "&:hover": { bgcolor: "var(--color-on-dark-faint)", color: "#fff" }, "&:disabled": { opacity: 0.5, cursor: "default" } };
  const timerColor = timeUp ? "#ff8a80" : lowTime ? "var(--color-warn)" : running ? "var(--color-accent-lime)" : "#fff";

  return (
    <Box>
      <Box sx={{ bgcolor: "var(--color-surface-night)", color: "#fff", borderRadius: "16px", p: "14px 20px", mb: 2, display: "flex", alignItems: "center", gap: "18px", flexWrap: "wrap" }}>
        {badgeLabel && (
          <Box sx={{ fontSize: 11, fontWeight: 800, letterSpacing: ".04em", px: "10px", py: "4px", borderRadius: "999px", bgcolor: resolvedBadgeColor, color: "#fff" }}>
            {badgeLabel}
          </Box>
        )}
        <Box sx={{ flex: 1, minWidth: 160 }}>
          <Typography sx={{ ...eyebrow, color: "var(--color-on-dark-muted)", opacity: 1 }}>Skill sheet</Typography>
          <Typography component="h1" sx={{ fontSize: 22, fontWeight: 600, lineHeight: 1.15, letterSpacing: "-.01em", fontFamily: "var(--font-display)" }}>{checklist?.title}</Typography>
        </Box>

        {headerRight && (
          <Box sx={{ display: "flex", alignItems: "center", gap: "16px", color: "#fff", "& .MuiTypography-root": { color: "inherit" } }}>
            {headerRight(ctx)}
          </Box>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "2px" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "4px", border: "1px solid var(--color-hairline-violet)", borderRadius: "12px", p: "4px 4px 4px 12px" }}>
            <AccessTimeIcon sx={{ fontSize: 16, color: timerColor }} />
            <Typography sx={{ fontSize: 20, fontWeight: 600, fontVariantNumeric: "tabular-nums", minWidth: 62, ml: "6px", color: timerColor, fontFamily: "var(--font-mono)" }}>{mm}:{ss}</Typography>
            {!locked && (
              <>
                <Box component="button" type="button" disabled={timeExpired && autoSubmitOnTimeout} onClick={toggleTimer} title={running ? "Pause" : "Start"} aria-label={running ? "Pause timer" : "Start timer"} sx={iconBtn}>
                  {running ? <PauseIcon sx={{ fontSize: 17 }} /> : <PlayArrowIcon sx={{ fontSize: 17 }} />}
                </Box>
                <Box component="button" type="button" onClick={resetTimer} title="Reset" aria-label="Reset timer" sx={iconBtn}>
                  <ReplayIcon sx={{ fontSize: 16 }} />
                </Box>
              </>
            )}
          </Box>
          {hasLimit && (
            <Typography sx={{ fontSize: 10.5, color: "var(--color-on-dark-muted)", fontWeight: 600 }}>
              {timeExpired ? (autoSubmitOnTimeout ? "Time's up — submitting…" : "Time's up") : `${checklist.timeLimitMin} min limit`}
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0,1fr)", md: "minmax(0,1fr) 300px" }, gap: 2.5, alignItems: "start" }}>
        <Box sx={{ minWidth: 0 }}>
          {items.length === 0 ? (
            <Box sx={{ borderRadius: "16px", border: `1px solid ${T.border}`, p: 4, textAlign: "center", bgcolor: "#fff" }}>
              <Typography variant="body2" color="text.secondary">No items on this checklist.</Typography>
            </Box>
          ) : (
            sections.map((sec, i) => (
              <SectionGroup key={sec.title + i} section={sec} sectionIdx={i} scores={scores} notes={notes} onScore={setScore} onNote={setNote} locked={locked || timeExpired} currentId={locked ? null : focusId} onFocusItem={setCurrentId} />
            ))
          )}
          {footer && footer(ctx)}
        </Box>

        <Box component="aside" sx={{ position: { md: "sticky" }, top: { md: 16 }, display: "grid", gap: "14px" }}>
          <Box aria-label="Result" sx={{ bgcolor: "#fff", border: `1px solid ${T.border}`, borderRadius: "16px", p: 2.5, display: "grid", gap: 2 }}>
            <Box>
              <Typography sx={eyebrow}>Score</Typography>
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
                <Typography component="b" sx={{ fontSize: 44, lineHeight: 1, fontWeight: 600, letterSpacing: "-.02em", fontFamily: "var(--font-display)" }}>{r.earned}</Typography>
                <Typography component="span" sx={{ fontSize: 14, opacity: 0.6 }}>of {r.total} pts{left ? ` · ${left} left` : ""}</Typography>
              </Box>
            </Box>
            <Box aria-hidden="true" sx={{ display: "grid", gridAutoFlow: "column", gridAutoColumns: "1fr", gap: "3px" }}>
              {items.map((it) => (
                <Box key={it.id} component="i" sx={{ height: 8, borderRadius: "2px", bgcolor: SEG_COLOR[scores[it.id]] || T.border }} />
              ))}
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", borderTop: `1px solid ${T.border}`, pt: 1.75 }}>
              {[
                { v: `${r.scoredCount}/${items.length}`, l: "Scored" },
                { v: `${criticalMet}/${criticalCount}`, l: "Critical met", bad: r.criticalsFailed.length > 0 },
                { v: String(missedCount), l: "Missed" },
              ].map((st, i) => (
                <Box key={st.l} sx={{ display: "grid", gap: "2px", pl: i ? 1.5 : 0, borderLeft: i ? `1px solid ${T.border}` : 0 }}>
                  <Typography component="b" sx={{ fontSize: 18, fontWeight: 600, fontFamily: "var(--font-display)", color: st.bad ? T.missed : T.ink }}>{st.v}</Typography>
                  <Typography component="span" sx={{ fontSize: 12, opacity: 0.6 }}>{st.l}</Typography>
                </Box>
              ))}
            </Box>
            <Box data-testid="checklist-verdict" sx={{ p: "12px 14px", borderRadius: "12px", bgcolor: verdict.bg, color: verdict.fg }}>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: "inherit" }}>{verdict.label}</Typography>
              <Typography sx={{ fontSize: 12.5, opacity: 0.8, color: "inherit" }}>
                {r.criticalsFailed.length ? `${r.criticalsFailed.length} critical step${r.criticalsFailed.length > 1 ? "s" : ""} missed.` : verdict.note}
              </Typography>
            </Box>
          </Box>
          {rail && rail(ctx)}
          {!locked && items.length > 0 && (
            <Typography sx={{ display: { xs: "none", md: "block" }, fontSize: 12, opacity: 0.65, px: 0.5 }}>
              Keys: P performed · M missed · N N/A · ↑ ↓ move
            </Typography>
          )}
        </Box>
      </Box>

      {bottomBar && bottomBar(ctx)}
    </Box>
  );
}
